import React from 'react';
import { clsx } from 'clsx';

interface StockIndicatorProps {
  current: number;
  max?: number;
  showBadge?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

type StockStatus = 'high' | 'medium' | 'low' | 'out';

const getStockStatus = (current: number, max: number = 5): StockStatus => {
  if (current === 0) return 'out';
  if (current === 1) return 'low';
  if (current >= 2 && current <= 4) return 'medium';
  return 'high';
};

const getStatusColor = (status: StockStatus) => {
  switch (status) {
    case 'high':
      return { bg: 'bg-green-100', text: 'text-green-700', dot: 'bg-green-500' };
    case 'medium':
      return { bg: 'bg-amber-100', text: 'text-amber-700', dot: 'bg-amber-500' };
    case 'low':
      return { bg: 'bg-red-100', text: 'text-red-700', dot: 'bg-red-600' };
    case 'out':
      return { bg: 'bg-gray-100', text: 'text-gray-600', dot: 'bg-gray-400' };
  }
};

const getUrgencyBadge = (status: StockStatus) => {
  switch (status) {
    case 'low':
      return '⚠️ ÚLTIMAS UNIDADES';
    case 'out':
      return '🔒 AGOTADO';
    default:
      return null;
  }
};

/**
 * Indicador de stock con sentido de urgencia
 * Muestra disponibilidad con color y badge contextual
 */
const StockIndicator: React.FC<StockIndicatorProps> = ({
  current,
  max = 5,
  showBadge = true,
  size = 'md',
  className = ''
}) => {
  const status = getStockStatus(current, max);
  const colors = getStatusColor(status);
  const badge = showBadge ? getUrgencyBadge(status) : null;
  
  const sizeClasses = {
    sm: 'text-xs py-1 px-2',
    md: 'text-sm py-1.5 px-3',
    lg: 'text-base py-2 px-4'
  };

  // Barra de progreso visual
  const barWidth = status === 'out' ? 0 : (current / max) * 100;

  return (
    <div className={className}>
      {/* Badge de urgencia */}
      {badge && (
        <div className={clsx(
          'inline-flex items-center px-3 py-1 rounded-full text-xs font-bold mb-2',
          status === 'low' && 'bg-red-100 text-red-700 animate-pulse',
          status === 'out' && 'bg-gray-200 text-gray-600'
        )}>
          {badge}
        </div>
      )}

      {/* Indicador principal */}
      <div className={clsx(
        'inline-flex items-center rounded-lg',
        sizeClasses[size],
        colors.bg,
        colors.text
      )}>
        <span className={clsx('h-2 w-2 rounded-full mr-2', colors.dot)} />
        <span className="font-semibold">
          {status === 'out' 
            ? 'Sin stock' 
            : `${current} ${current === 1 ? 'unidad' : 'unidades'}`
          }
        </span>
      </div>

      {/* Barra visual de stock */}
      <div className="mt-2 h-1 bg-gray-200 rounded-full overflow-hidden">
        <div
          className={clsx(
            'h-full transition-all duration-500',
            status === 'high' && 'bg-green-500',
            status === 'medium' && 'bg-amber-500',
            status === 'low' && 'bg-red-600',
            status === 'out' && 'bg-gray-300'
          )}
          style={{ width: `${barWidth}%` }}
        />
      </div>

      {/* Texto descriptivo */}
      <p className="text-xs text-gray-500 mt-1">
        {status === 'out' && 'Producto no disponible en este momento'}
        {status === 'low' && 'Stock limitado, ¡apresúrate!'}
        {status === 'medium' && 'Stock moderado'}
        {status === 'high' && 'Buena disponibilidad'}
      </p>
    </div>
  );
};

export default StockIndicator;
